import { useState } from 'react'
import {
  Modal,
  Steps,
  Upload,
  Button,
  Alert,
  Table,
  Progress,
  Typography,
  Space,
  Tag,
  Switch,
  Tooltip,
  Checkbox,
  message,
} from 'antd'
import {
  UploadOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { UploadFile, UploadProps } from 'antd'
import { hospitalImportApi } from '@/api/hospital-import'
import type { ValidationResult, ImportResult, RowPreview } from '@/types/hospital-import'
import Papa from 'papaparse'

interface HospitalImportWizardProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const { Title, Text } = Typography

export const HospitalImportWizard: React.FC<HospitalImportWizardProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [previewData, setPreviewData] = useState<Record<string, string>[]>([])
  const [dryRun, setDryRun] = useState(false)
  const [skipInvalidRows, setSkipInvalidRows] = useState(false)

  const handleReset = () => {
    setCurrentStep(0)
    setFile(null)
    setFileList([])
    setValidationResult(null)
    setImportResult(null)
    setPreviewData([])
    setDryRun(false)
    setSkipInvalidRows(false)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  const uploadProps: UploadProps = {
    accept: '.csv',
    maxCount: 1,
    fileList,
    beforeUpload: (uploadFile) => {
      // Validate file type
      if (!uploadFile.name.endsWith('.csv')) {
        message.error('Please upload a CSV file')
        return false
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (uploadFile.size > maxSize) {
        message.error('File size must be less than 10MB')
        return false
      }

      setFile(uploadFile)
      setFileList([uploadFile as UploadFile])

      // Parse CSV for preview (first 10 rows)
      Papa.parse(uploadFile, {
        header: true,
        skipEmptyLines: true,
        preview: 10,
        complete: (results) => {
          setPreviewData(results.data as Record<string, string>[])
        },
      })

      return false // Prevent auto upload
    },
    onRemove: () => {
      setFile(null)
      setFileList([])
      setPreviewData([])
    },
  }

  const handleValidate = async () => {
    if (!file) {
      message.error('Please select a file')
      return
    }

    setLoading(true)
    try {
      const result = await hospitalImportApi.validateImport(file)
      setValidationResult(result)
      setCurrentStep(1)
      message.success('Validation complete')
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
            'Validation failed'
          : 'Validation failed'
      message.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    if (!file) {
      message.error('Please select a file')
      return
    }

    setLoading(true)
    setCurrentStep(2) // Move to progress step

    try {
      const result = await hospitalImportApi.importHospitals(file, dryRun, skipInvalidRows)
      setImportResult(result)
      setCurrentStep(3) // Move to summary step

      if (dryRun) {
        message.success('Preview complete - no data was modified')
      } else {
        message.success('Import complete')
        onSuccess?.()
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
            'Import failed'
          : 'Import failed'
      message.error(errorMessage)
      setCurrentStep(1) // Go back to validation step
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const blob = await hospitalImportApi.downloadTemplate()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'hospital-import-template.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      message.success('Template downloaded')
    } catch (_error) {
      message.error('Failed to download template')
    }
  }

  const steps = [
    {
      title: 'Upload',
      icon: <UploadOutlined />,
    },
    {
      title: 'Validate',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Import',
      icon: <FileTextOutlined />,
    },
    {
      title: 'Summary',
      icon: <CheckCircleOutlined />,
    },
  ]

  const renderUploadStep = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        message="Import Instructions"
        description={
          <div>
            <p>Upload a CSV file with hospital data. The file must include:</p>
            <ul>
              <li>
                Required columns: <code>cmsid, name, city, state</code>
              </li>
              <li>Maximum file size: 10MB</li>
              <li>Existing hospitals (by CMSID or ID) will be updated</li>
              <li>
                <strong>Dry Run Mode:</strong> Preview changes without modifying data
              </li>
            </ul>
            <Button type="link" onClick={handleDownloadTemplate}>
              Download CSV Template
            </Button>
          </div>
        }
        type="info"
        showIcon
      />

      <Space
        direction="horizontal"
        align="center"
        style={{
          padding: '12px 16px',
          background: '#f5f5f5',
          borderRadius: '6px',
        }}
      >
        <Switch checked={dryRun} onChange={setDryRun} />
        <Text strong>Dry Run Mode (Preview Only)</Text>
        <Tooltip title="Preview what would happen without actually importing data. Shows which hospitals would be created or updated.">
          <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
        </Tooltip>
        {dryRun && <Tag color="orange">Preview Mode - No data will be modified</Tag>}
      </Space>

      {!file && (
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">Click or drag CSV file to this area</p>
          <p className="ant-upload-hint">Only .csv files are accepted (max 10MB)</p>
        </Upload.Dragger>
      )}

      {file && (
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>Change File</Button>
        </Upload>
      )}

      {previewData.length > 0 && (
        <div>
          <Title level={5}>Preview (first 10 rows)</Title>
          <Table
            size="small"
            dataSource={previewData}
            columns={Object.keys(previewData[0]).map((key) => ({
              title: key,
              dataIndex: key,
              key,
              ellipsis: true,
              width: 150,
            }))}
            scroll={{ x: 'max-content' }}
            pagination={false}
          />
        </div>
      )}
    </Space>
  )

  const renderValidationStep = () => {
    if (!validationResult) return null

    const { success, errors, warnings, validRows, totalRows } = validationResult
    const hasErrors = errors.length > 0
    const hasWarnings = warnings.length > 0

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {success && !hasErrors && (
          <Alert
            message="Validation Successful"
            description={`All ${validRows} rows are valid and ready for import`}
            type="success"
            showIcon
          />
        )}

        {hasErrors && (
          <>
            <Alert
              message="Validation Errors"
              description={`Found ${errors.length} errors in ${totalRows} rows. Please fix these before importing.`}
              type="error"
              showIcon
            />
            <Checkbox
              checked={skipInvalidRows}
              onChange={(e) => setSkipInvalidRows(e.target.checked)}
              style={{ marginTop: 8 }}
            >
              Skip rows with errors and import valid rows only ({validRows} valid rows)
            </Checkbox>
          </>
        )}

        {hasWarnings && (
          <Alert
            message="Validation Warnings"
            description={`Found ${warnings.length} warnings. These are informational and won't prevent import.`}
            type="warning"
            showIcon
          />
        )}

        {hasErrors && (
          <div>
            <Title level={5}>Errors</Title>
            <Table
              size="small"
              dataSource={errors}
              columns={[
                {
                  title: 'Row',
                  dataIndex: 'row',
                  key: 'row',
                  width: 80,
                },
                {
                  title: 'Field',
                  dataIndex: 'field',
                  key: 'field',
                  width: 150,
                },
                {
                  title: 'Value',
                  dataIndex: 'value',
                  key: 'value',
                  width: 150,
                  render: (value) => value || <Text type="secondary">(empty)</Text>,
                },
                {
                  title: 'Error',
                  dataIndex: 'error',
                  key: 'error',
                },
              ]}
              pagination={{ pageSize: 10 }}
            />
          </div>
        )}

        {hasWarnings && (
          <div>
            <Title level={5}>Warnings</Title>
            <Table
              size="small"
              dataSource={warnings}
              columns={[
                {
                  title: 'Row',
                  dataIndex: 'row',
                  key: 'row',
                  width: 80,
                },
                {
                  title: 'Message',
                  dataIndex: 'message',
                  key: 'message',
                },
              ]}
              pagination={{ pageSize: 10 }}
            />
          </div>
        )}
      </Space>
    )
  }

  const renderProgressStep = () => (
    <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
      <Progress type="circle" percent={100} status="active" />
      <Text>Importing hospitals...</Text>
    </Space>
  )

  const renderSummaryStep = () => {
    if (!importResult) return null

    const {
      success,
      created,
      updated,
      failed,
      skipped,
      errors,
      dryRun: isDryRun,
      preview,
    } = importResult
    const total = created + updated + failed + (skipped ?? 0)
    const successRate = total > 0 ? Math.round(((created + updated) / total) * 100) : 0

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {isDryRun && (
          <Alert
            message="Dry Run Preview Complete"
            description="This was a preview only - no data was modified. Review the changes below and run without Dry Run mode to apply them."
            type="info"
            showIcon
          />
        )}

        {!isDryRun && success && failed === 0 && (
          <Alert
            message="Import Successful"
            description={`Successfully imported ${created + updated} hospitals`}
            type="success"
            showIcon
          />
        )}

        {!isDryRun && failed > 0 && (
          <Alert
            message="Import Completed with Errors"
            description={`${created + updated} hospitals imported successfully, ${failed} failed`}
            type="warning"
            showIcon
          />
        )}

        <div>
          <Title level={5}>Import Statistics</Title>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Total Processed:</Text>
              <Tag>{total}</Tag>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Created:</Text>
              <Tag color="success">{created}</Tag>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Updated:</Text>
              <Tag color="blue">{updated}</Tag>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Failed:</Text>
              <Tag color="error">{failed}</Tag>
            </div>
            {skipped !== undefined && skipped > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Skipped:</Text>
                <Tag color="orange">{skipped}</Tag>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Success Rate:</Text>
              <Tag color={successRate === 100 ? 'success' : 'warning'}>{successRate}%</Tag>
            </div>
          </Space>
        </div>

        {errors.length > 0 && (
          <div>
            <Title level={5}>Errors</Title>
            <Table
              size="small"
              dataSource={errors}
              columns={[
                {
                  title: 'Row',
                  dataIndex: 'row',
                  key: 'row',
                  width: 80,
                },
                {
                  title: 'CMSID',
                  dataIndex: 'cmsid',
                  key: 'cmsid',
                  width: 120,
                },
                {
                  title: 'Error',
                  dataIndex: 'error',
                  key: 'error',
                },
              ]}
              pagination={{ pageSize: 10 }}
            />
          </div>
        )}

        {isDryRun && preview && preview.length > 0 && (
          <div>
            <Title level={5}>
              Preview of Changes {preview.length > 20 && `(showing first 20 of ${preview.length})`}
            </Title>
            <Table
              size="small"
              dataSource={preview.slice(0, 20)}
              rowKey="row"
              columns={[
                {
                  title: 'Row',
                  dataIndex: 'row',
                  key: 'row',
                  width: 70,
                },
                {
                  title: 'CMSID',
                  dataIndex: 'cmsid',
                  key: 'cmsid',
                  width: 100,
                },
                {
                  title: 'Name',
                  dataIndex: 'name',
                  key: 'name',
                  ellipsis: true,
                },
                {
                  title: 'Action',
                  dataIndex: 'action',
                  key: 'action',
                  width: 100,
                  render: (action: 'create' | 'update') => (
                    <Tag color={action === 'create' ? 'green' : 'blue'}>{action.toUpperCase()}</Tag>
                  ),
                },
                {
                  title: 'Match Method',
                  dataIndex: 'matchedBy',
                  key: 'matchedBy',
                  width: 120,
                  render: (matchedBy?: 'id' | 'cmsid') =>
                    matchedBy ? (
                      <Tag>{matchedBy.toUpperCase()}</Tag>
                    ) : (
                      <Text type="secondary">New</Text>
                    ),
                },
                {
                  title: 'Details',
                  key: 'details',
                  width: 180,
                  render: (record: RowPreview) => (
                    <Space direction="vertical" size="small">
                      {record.existingId && (
                        <Text
                          type="secondary"
                          style={{ fontSize: '12px' }}
                          ellipsis
                          title={`Existing ID: ${record.existingId}`}
                        >
                          Existing: {record.existingId.slice(0, 8)}...
                        </Text>
                      )}
                      {record.providedId && (
                        <Text
                          type="secondary"
                          style={{ fontSize: '12px' }}
                          ellipsis
                          title={`Provided ID: ${record.providedId}`}
                        >
                          Provided: {record.providedId.slice(0, 8)}...
                        </Text>
                      )}
                    </Space>
                  ),
                },
              ]}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}
      </Space>
    )
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderUploadStep()
      case 1:
        return renderValidationStep()
      case 2:
        return renderProgressStep()
      case 3:
        return renderSummaryStep()
      default:
        return null
    }
  }

  const getFooterButtons = () => {
    switch (currentStep) {
      case 0:
        return [
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="validate"
            type="primary"
            onClick={handleValidate}
            disabled={!file}
            loading={loading}
          >
            Validate
          </Button>,
        ]
      case 1:
        return [
          <Button key="back" onClick={() => setCurrentStep(0)}>
            Back
          </Button>,
          <Button
            key="import"
            type="primary"
            onClick={handleImport}
            disabled={(validationResult?.errors.length ?? 0) > 0 && !skipInvalidRows}
            loading={loading}
          >
            {dryRun ? 'Preview Changes' : 'Import'}
          </Button>,
        ]
      case 2:
        return [] // No buttons during import
      case 3:
        if (importResult?.dryRun) {
          return [
            <Button key="close" onClick={handleClose}>
              Close
            </Button>,
            <Button
              key="import"
              type="primary"
              onClick={() => {
                setDryRun(false)
                setCurrentStep(1)
              }}
            >
              Run Actual Import
            </Button>,
          ]
        }
        return [
          <Button key="close" type="primary" onClick={handleClose}>
            Close
          </Button>,
        ]
      default:
        return []
    }
  }

  return (
    <Modal
      title="Import Hospitals"
      open={open}
      onCancel={handleClose}
      footer={getFooterButtons()}
      width={800}
      maskClosable={false}
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />
      {renderStepContent()}
    </Modal>
  )
}
